import { useState, useEffect, useCallback } from "react";
import {
  getTreasuryStatus, startTreasury, stopTreasury, distributeNow,
  resetCircuitBreaker, resetGasHalt, getTreasuryHistory, getWatchdogStatus, triggerWatchdog,
  withdrawPaypal, withdrawCoinspot, syncCoinspot, coinspotBalances,
  getBankSummary, triggerBankRebate, updateBankSettings,
  getPayPalBalance, getVaultAudit, getRealBalance,
  getYieldStatus, updateYieldSettings, runYieldCycle,
  getPayoutProposals, approvePayoutProposal, rejectPayoutProposal
} from "@/goldhunter/api/entities";
import {
  Vault, Play, Square, Banknote, ShieldAlert, ShieldCheck,
  RefreshCw, ArrowDownToLine, AlertTriangle, Heart, Activity,
  Bell, Fuel, CreditCard, Building2, PiggyBank, CalendarClock, TrendingDown,
  Settings as SettingsIcon, Search, ChevronDown, ChevronRight
} from "lucide-react";

export default function Treasury() {
  const [treasury, setTreasury] = useState(null);
  const [history, setHistory] = useState([]);
  const [watchdog, setWatchdog] = useState(null);
  const [bank, setBank] = useState(null);
  const [paypal, setPaypal] = useState(null);
  const [paypalErr, setPaypalErr] = useState(null);
  const [audit, setAudit] = useState(null);
  const [auditErr, setAuditErr] = useState(null);
  const [yieldData, setYieldData] = useState(null);
  const [yieldErr, setYieldErr] = useState(null);
  const [realBal, setRealBal] = useState(null);
  const [realBalErr, setRealBalErr] = useState(null);
  const [proposals, setProposals] = useState(null);
  const [proposalsErr, setProposalsErr] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("overview");

  const load = useCallback(async () => {
    try {
      const [t, h, w, b] = await Promise.all([
        getTreasuryStatus(),
        getTreasuryHistory(),
        getWatchdogStatus(),
        getBankSummary(),
      ]);
      setTreasury(t);
      setHistory(Array.isArray(h) ? h : []);
      setWatchdog(w);
      setBank(b);
    } catch (e) { console.warn(e); }
    finally { setLoading(false); }
    // PayPal live balance is fetched separately so a failure here does not
    // hide the rest of the dashboard. Per policy: surface the error, never
    // fall back to fake/cached numbers.
    try {
      const p = await getPayPalBalance();
      setPaypal(p);
      setPaypalErr(null);
    } catch (e) {
      setPaypal(null);
      setPaypalErr(e?.response?.data?.detail || e.message || "PayPal balance unavailable");
    }
    try {
      const rb = await getRealBalance();
      setRealBal(rb);
      setRealBalErr(null);
    } catch (e) {
      setRealBal(null);
      setRealBalErr(e?.response?.data?.detail || e.message || "Real balance check failed");
    }
    try {
      const pr = await getPayoutProposals({ limit: 50 });
      setProposals(Array.isArray(pr?.proposals) ? pr.proposals : []);
      setProposalsErr(null);
    } catch (e) {
      setProposals(null);
      setProposalsErr(e?.response?.data?.detail || e.message || "Could not load payout proposals");
    }
    try {
      const ys = await getYieldStatus();
      setYieldData(ys);
      setYieldErr(null);
    } catch (e) {
      setYieldData(null);
      setYieldErr(e?.response?.data?.detail || e.message || "Yield status unavailable");
    }
  }, []);

  // Vault audit aggregates 100k rows server-side — only fetch when its tab
  // is active, on a slower 30s cadence.
  const loadAudit = useCallback(async () => {
    try {
      const a = await getVaultAudit();
      setAudit(a);
      setAuditErr(null);
    } catch (e) {
      setAudit(null);
      setAuditErr(e?.response?.data?.detail || e.message || "Vault audit unavailable");
    }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { const iv = setInterval(load, 10000); return () => clearInterval(iv); }, [load]);
  useEffect(() => {
    if (tab !== "audit") return;
    loadAudit();
    const iv = setInterval(loadAudit, 30000);
    return () => clearInterval(iv);
  }, [tab, loadAudit]);

  const handleStart = async () => { await startTreasury(); load(); };
  const handleStop = async () => { await stopTreasury(); load(); };
  const handleDistribute = async () => { await distributeNow(); setTimeout(load, 2000); };
  const handleResetBreaker = async () => { await resetCircuitBreaker(); load(); };
  const handleResetGasHalt = async () => { await resetGasHalt(); load(); };
  const handleWatchdog = async () => { await triggerWatchdog(); setTimeout(load, 1000); };

  if (loading) return (
    <div className="flex items-center justify-center py-32">
      <div className="w-5 h-5 border-2 border-white/10 border-t-amber-400 rounded-full animate-spin" />
    </div>
  );

  const t = treasury || {};
  const tierLabel = t.initial_complete ? "Tier 2: 5% Passive Stream" : t.net_profit >= 100 ? "Tier 1: 30% Initial Payday" : "Tier 0: Compounding";
  const tierColor = t.initial_complete ? "#22c55e" : t.net_profit >= 100 ? "var(--yb-gold)" : "var(--yb-text-muted)";
  const compoundPct = t.initial_complete ? 95 : (t.net_profit >= 100 ? 70 : 100);

  return (
    <div data-testid="treasury-page" className="max-w-6xl mx-auto px-4 py-6 space-y-5">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 mb-2">
            <Banknote size={18} style={{ color: "var(--yb-gold)" }} />
            <span className="text-[10px] font-bold tracking-widest uppercase" style={{ color: "var(--yb-gold)" }}>
              Sovereign Vault
            </span>
            {t.circuit_breaker && (
              <span data-testid="circuit-breaker-alert" className="text-[10px] font-mono px-2 py-0.5 rounded-sm animate-pulse" style={{ color: "#ef4444", background: "rgba(239,68,68,0.1)", border: "1px solid rgba(239,68,68,0.3)" }}>
                CIRCUIT BREAKER
              </span>
            )}
            {t.gas_floor_halt && (
              <span data-testid="gas-halt-alert" className="text-[10px] font-mono px-2 py-0.5 rounded-sm animate-pulse" style={{ color: "#ef4444", background: "rgba(239,68,68,0.15)", border: "1px solid rgba(239,68,68,0.4)" }}>
                GAS FLOOR HALT
              </span>
            )}
            <span className="text-[10px] font-mono px-2 py-0.5 rounded-sm" style={{
              color: t.running ? "#22c55e" : "var(--yb-text-muted)",
              background: t.running ? "rgba(34,197,94,0.1)" : "rgba(255,255,255,0.04)",
              border: `1px solid ${t.running ? "rgba(34,197,94,0.2)" : "var(--yb-border)"}`,
            }}>
              {t.running ? "ACTIVE" : "STANDBY"}
            </span>
          </div>
          <h1 className="text-2xl font-black tracking-tight" style={{ color: "var(--yb-text-primary)" }}>
            Tiered Profit Distribution
          </h1>
          <p className="text-xs mt-1" style={{ color: "var(--yb-text-muted)" }}>
            30% initial payday / 5% passive stream / 95% compound working capital
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button data-testid="btn-distribute-now" onClick={handleDistribute}
            className="flex items-center gap-1.5 text-[11px] font-medium px-3 py-2 rounded-sm transition-all"
            style={{ background: "rgba(255,184,0,0.1)", color: "var(--yb-gold)", border: "1px solid rgba(255,184,0,0.2)" }}>
            <ArrowDownToLine size={12} /> Distribute Now
          </button>
          {t.running ? (
            <button data-testid="btn-stop-treasury" onClick={handleStop}
              className="flex items-center gap-1.5 text-[11px] font-medium px-3 py-2 rounded-sm"
              style={{ background: "rgba(239,68,68,0.1)", color: "#ef4444", border: "1px solid rgba(239,68,68,0.2)" }}>
              <Square size={12} /> Stop Treasury
            </button>
          ) : (
            <button data-testid="btn-start-treasury" onClick={handleStart}
              className="flex items-center gap-1.5 text-[11px] font-bold px-4 py-2 rounded-sm"
              style={{ background: "var(--yb-gold)", color: "#000" }}>
              <Play size={12} /> Start Treasury
            </button>
          )}
        </div>
      </div>

      {/* Tier Status Banner */}
      <div className="rounded-sm p-5" style={{
        background: `linear-gradient(135deg, ${tierColor}08 0%, transparent 100%)`,
        border: `1px solid ${tierColor}30`,
      }}>
        <div className="flex items-center justify-between">
          <div>
            <p className="text-[10px] font-bold tracking-widest uppercase mb-1" style={{ color: tierColor }}>
              Current Distribution Tier
            </p>
            <p className="text-lg font-black" style={{ color: "var(--yb-text-primary)" }}>{tierLabel}</p>
            <p className="text-xs mt-1" style={{ color: "var(--yb-text-muted)" }}>
              {t.initial_complete
                ? "Taking 5% of net profit. 95% reinvested into Gold-Hunter fleet."
                : t.net_profit >= 100
                  ? "Net profit exceeds $100. Ready for 30% initial withdrawal."
                  : `Net profit: $${(t.net_profit || 0).toFixed(2)}. Need $100+ to trigger Tier 1.`
              }
            </p>
          </div>
          <div className="text-right">
            <div className="text-3xl font-black font-mono" style={{ color: "var(--yb-gold)" }}>
              ${Math.round(t.net_profit || 0).toLocaleString()}
            </div>
            <div className="text-[10px] uppercase tracking-widest" style={{ color: "var(--yb-text-muted)" }}>Net Profit (Post-Tax)</div>
          </div>
        </div>

        {/* Distribution bar */}
        <div className="mt-4 flex gap-1 h-3 rounded-sm overflow-hidden">
          {t.initial_complete ? (
            <>
              <div className="rounded-sm" style={{ width: "5%", background: "var(--yb-gold)" }} title="5% Withdrawal" />
              <div className="rounded-sm" style={{ width: "95%", background: "rgba(34,197,94,0.3)" }} title="95% Compounding" />
            </>
          ) : t.net_profit >= 100 ? (
            <>
              <div className="rounded-sm" style={{ width: "30%", background: "var(--yb-gold)" }} title="30% Initial Payday" />
              <div className="rounded-sm" style={{ width: "70%", background: "rgba(34,197,94,0.3)" }} title="70% Working Capital" />
            </>
          ) : (
            <div className="rounded-sm w-full" style={{ background: "rgba(34,197,94,0.3)" }} title="100% Compounding" />
          )}
        </div>
        <div className="flex justify-between mt-1">
          <span className="text-[10px] font-mono" style={{ color: "var(--yb-gold)" }}>
            {t.initial_complete ? "5% withdrawal" : t.net_profit >= 100 ? "30% withdrawal" : "0% — compounding"}
          </span>
          <span className="text-[10px] font-mono" style={{ color: "#22c55e" }}>
            {compoundPct}% reinvested
          </span>
        </div>
      </div>

      {/* SAFETY BANNER — vault figures are not real money */}
      <div data-testid="vault-safety-banner" className="rounded-sm p-4"
        style={{ background: "rgba(239,68,68,0.06)", border: "1px solid rgba(239,68,68,0.3)" }}>
        <div className="flex items-start gap-2">
          <AlertTriangle size={16} className="shrink-0 mt-0.5" style={{ color: "#ef4444" }} />
          <div>
            <p className="text-xs font-bold uppercase tracking-widest mb-1" style={{ color: "#ef4444" }}>
              Vault figures are logged income entries — not confirmed funds
            </p>
            <p className="text-[11px]" style={{ color: "var(--yb-text-secondary)" }}>
              Confirmed withdrawals to date: <span className="font-mono font-bold">$0.00</span>.
              Automated payouts are <span className="font-bold">blocked</span> until at least one
              real-balance API (PayPal / Stripe / CoinSpot) confirms available funds &gt; payout amount.
            </p>
          </div>
        </div>
      </div>

      {/* Payout proposals — the only place money movement gets approved */}
      <PayoutProposalsPanel proposals={proposals} error={proposalsErr} onResolved={load} />

      {/* Real Balance — multi-rail live API check; never substitutes vault totals */}
      <RealBalanceCard data={realBal} error={realBalErr} />

      {/* Live PayPal Balance — real /v1/reporting/balances, no mocks */}
      <div data-testid="paypal-live-balance-card" className="rounded-sm p-4"
        style={{ background: "var(--yb-surface-1)", border: "1px solid var(--yb-border)" }}>
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <CreditCard size={14} style={{ color: "var(--yb-gold)" }} />
            <span className="text-xs uppercase tracking-widest" style={{ color: "var(--yb-text-muted)" }}>
              PayPal LIVE Balance
            </span>
          </div>
          <span className="text-[9px] font-mono" style={{ color: paypalErr ? "#ef4444" : "#22c55e" }}>
            {paypalErr ? "ERROR" : (paypal ? "LIVE" : "LOADING")}
          </span>
        </div>
        {paypalErr ? (
          <div className="text-xs font-mono" style={{ color: "#ef4444" }} data-testid="paypal-balance-error">
            {paypalErr}
          </div>
        ) : paypal ? (
          <div className="grid grid-cols-3 gap-3">
            <div>
              <div className="text-2xl font-black font-mono" style={{ color: "#22c55e" }} data-testid="paypal-available">
                ${paypal.available_aud.toFixed(2)}
              </div>
              <div className="text-[9px] uppercase tracking-widest mt-1" style={{ color: "var(--yb-text-muted)" }}>
                Available ({paypal.currency})
              </div>
            </div>
            <div>
              <div className="text-2xl font-black font-mono" style={{ color: "var(--yb-gold)" }}>
                ${paypal.pending_aud.toFixed(2)}
              </div>
              <div className="text-[9px] uppercase tracking-widest mt-1" style={{ color: "var(--yb-text-muted)" }}>
                Pending
              </div>
            </div>
            <div>
              <div className="text-xs font-mono mt-2" style={{ color: "var(--yb-text-primary)" }}>
                {new Date(paypal.last_synced).toLocaleString()}
              </div>
              <div className="text-[9px] uppercase tracking-widest mt-1" style={{ color: "var(--yb-text-muted)" }}>
                Last Synced
              </div>
            </div>
          </div>
        ) : (
          <div className="text-xs font-mono" style={{ color: "var(--yb-text-muted)" }}>Querying PayPal…</div>
        )}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-5 gap-2">
        {[
          { v: `$${Math.round(t.total_withdrawn || 0).toLocaleString()}`, l: "Total Withdrawn", c: "var(--yb-gold)" },
          { v: `$${Math.round(t.net_profit || 0).toLocaleString()}`, l: "Net Profit", c: "#22c55e" },
          { v: t.tier || 0, l: "Current Tier", c: "var(--yb-text-primary)" },
          { v: t.consecutive_failures || 0, l: "Failed Attempts", c: (t.consecutive_failures || 0) > 0 ? "#ef4444" : "var(--yb-text-primary)" },
          { v: t.destination_configured ? "SET" : "NOT SET", l: "Destination", c: t.destination_configured ? "#22c55e" : "#ef4444" },
        ].map((s) => (
          <div key={s.l} className="rounded-sm p-3" style={{ background: "var(--yb-surface-1)", border: "1px solid var(--yb-border)" }}>
            <div className="text-lg font-black font-mono" style={{ color: s.c }}>{s.v}</div>
            <div className="text-[9px] uppercase tracking-widest mt-1" style={{ color: "var(--yb-text-muted)" }}>{s.l}</div>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div className="flex gap-1 p-1 rounded-sm w-fit" style={{ background: "rgba(255,255,255,0.03)" }}>
        {[["overview", "Treasury Logs"], ["history", "Withdrawal History"], ["withdraw", "Manual Withdraw"], ["bank", "Tax Bank"], ["yield", "Yield"], ["watchdog", "Watchdog"], ["safety", "Safety"], ["audit", "Projected / Logged Entries"]].map(([id, l]) => (
          <button key={id} onClick={() => setTab(id)} data-testid={`treasury-tab-${id}`}
            className="text-xs px-4 py-2 rounded-sm font-medium transition-all"
            style={{ background: tab === id ? "rgba(255,255,255,0.1)" : "transparent", color: tab === id ? "#fff" : "var(--yb-text-muted)" }}>
            {l}
          </button>
        ))}
      </div>

      {/* Treasury Logs */}
      {tab === "overview" && (
        <div className="rounded-sm p-4" style={{ background: "var(--yb-surface-1)", border: "1px solid var(--yb-border)" }}>
          <p className="text-[10px] font-bold uppercase tracking-widest mb-3" style={{ color: "var(--yb-gold)" }}>Distribution Log</p>
          <div className="space-y-0.5 max-h-64 overflow-y-auto">
            {(t.log_entries || []).map((entry, i) => (
              <p key={i} className="text-[11px] font-mono leading-relaxed" style={{
                color: entry.includes("ERROR") || entry.includes("FAILED") ? "#ef4444"
                  : entry.includes("OK") || entry.includes("COMPLETE") ? "#22c55e"
                    : entry.includes("TIER") ? "var(--yb-gold)"
                      : "var(--yb-text-muted)",
              }}>
                {entry}
              </p>
            ))}
            {(!t.log_entries || t.log_entries.length === 0) && (
              <p className="text-xs" style={{ color: "var(--yb-text-muted)" }}>No treasury activity yet. Start the treasury or trigger a manual distribution.</p>
            )}
          </div>
        </div>
      )}

      {/* Withdrawal History */}
      {tab === "history" && (
        <div className="space-y-1.5">
          {history.length === 0 ? (
            <p className="text-center py-16 text-sm" style={{ color: "var(--yb-text-muted)" }}>
              No withdrawals yet. Net profit needs to exceed $100 to trigger Tier 1.
            </p>
          ) : (
            history.map((w, i) => (
              <div key={i} className="rounded-sm p-4 flex items-center gap-4" style={{ background: "var(--yb-surface-1)", border: "1px solid var(--yb-border)" }}>
                <ArrowDownToLine size={16} style={{ color: "var(--yb-gold)" }} />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium" style={{ color: "var(--yb-text-primary)" }}>{w.source}</p>
                  <p className="text-[10px] font-mono mt-0.5" style={{ color: "var(--yb-text-muted)" }}>
                    {w.network} · TX: {w.tx_hash?.slice(0, 16)}... · {w.created_date?.slice(0, 19)}
                  </p>
                </div>
                <div className="text-lg font-black font-mono" style={{ color: "var(--yb-gold)" }}>
                  ${Math.round(w.amount).toLocaleString()}
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* Yield card on overview */}
      {tab === "overview" && <YieldOverviewCard data={yieldData} error={yieldErr} />}

      {/* Yield Panel */}
      {tab === "yield" && (
        <YieldPanel data={yieldData} error={yieldErr} onChange={load} />
      )}

      {/* Tax Bank Panel */}
      {tab === "bank" && (
        <BankPanel
          bank={bank}
          onTriggerRebate={async () => {
            const result = await triggerBankRebate();
            setTimeout(load, 1000);
            return result;
          }}
          onSettingsSaved={load}
        />
      )}

      {/* Manual Withdraw */}
      {tab === "withdraw" && (
        <ManualWithdraw onDone={load} />
      )}

      {/* Watchdog */}
      {tab === "watchdog" && watchdog && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Heart size={14} style={{ color: watchdog.issues_detected?.length > 0 ? "#ef4444" : "#22c55e" }} />
              <span className="text-xs font-bold" style={{ color: "var(--yb-text-primary)" }}>System Health Monitor</span>
            </div>
            <button data-testid="btn-watchdog-check" onClick={handleWatchdog}
              className="text-[10px] font-medium px-3 py-1.5 rounded-sm"
              style={{ background: "rgba(255,255,255,0.04)", color: "var(--yb-text-secondary)", border: "1px solid var(--yb-border)" }}>
              <RefreshCw size={10} className="inline mr-1" /> Run Check
            </button>
          </div>

          <div className="grid grid-cols-3 gap-2">
            {[
              { v: watchdog.health_checks, l: "Health Checks", c: "var(--yb-text-primary)" },
              { v: watchdog.auto_restarts, l: "Auto-Restarts", c: watchdog.auto_restarts > 0 ? "#f59e0b" : "var(--yb-text-primary)" },
              { v: (watchdog.issues_detected || []).length, l: "Active Issues", c: (watchdog.issues_detected || []).length > 0 ? "#ef4444" : "#22c55e" },
            ].map((s) => (
              <div key={s.l} className="rounded-sm p-3" style={{ background: "var(--yb-surface-1)", border: "1px solid var(--yb-border)" }}>
                <div className="text-lg font-black font-mono" style={{ color: s.c }}>{s.v}</div>
                <div className="text-[9px] uppercase tracking-widest mt-1" style={{ color: "var(--yb-text-muted)" }}>{s.l}</div>
              </div>
            ))}
          </div>

          {(watchdog.issues_detected || []).length > 0 && (
            <div className="rounded-sm p-4" style={{ background: "rgba(239,68,68,0.05)", border: "1px solid rgba(239,68,68,0.15)" }}>
              <p className="text-[10px] font-bold uppercase tracking-widest mb-2" style={{ color: "#ef4444" }}>Active Issues</p>
              {watchdog.issues_detected.map((issue, i) => (
                <div key={i} className="flex items-center gap-2 py-1.5" style={{ borderBottom: "1px solid rgba(239,68,68,0.1)" }}>
                  <AlertTriangle size={12} style={{ color: issue.severity === "critical" ? "#ef4444" : "#f59e0b" }} />
                  <span className="text-xs font-mono" style={{ color: "var(--yb-text-secondary)" }}>{issue.component}: {issue.error}</span>
                  <span className="text-[10px] ml-auto" style={{ color: issue.severity === "critical" ? "#ef4444" : "#f59e0b" }}>{issue.severity}</span>
                </div>
              ))}
            </div>
          )}

          <div className="rounded-sm p-4" style={{ background: "var(--yb-surface-1)", border: "1px solid var(--yb-border)" }}>
            <p className="text-[10px] font-bold uppercase tracking-widest mb-2" style={{ color: "var(--yb-text-muted)" }}>Watchdog Log</p>
            {(watchdog.log_entries || []).map((entry, i) => (
              <p key={i} className="text-[11px] font-mono leading-relaxed" style={{
                color: entry.includes("issues") ? "#f59e0b" : entry.includes("OK") ? "#22c55e" : "var(--yb-text-muted)",
              }}>{entry}</p>
            ))}
          </div>
        </div>
      )}

      {/* Safety Tab */}
      {tab === "safety" && (
        <div className="space-y-3">
          {/* Gas Floor Halt Banner */}
          {t.gas_floor_halt && (
            <div className="rounded-sm p-5" style={{ background: "rgba(239,68,68,0.08)", border: "1px solid rgba(239,68,68,0.25)" }}>
              <div className="flex items-center gap-2 mb-2">
                <Fuel size={16} style={{ color: "#ef4444" }} />
                <span className="text-sm font-black" style={{ color: "#ef4444" }}>GAS FLOOR BREACH — ALL WITHDRAWALS FROZEN</span>
              </div>
              <p className="text-xs mb-3" style={{ color: "var(--yb-text-secondary)" }}>
                Wallet balance dropped below {t.gas_hard_floor_eth || 0.005} ETH on Base. No automated resume.
                Top up your wallet, then override below.
              </p>
              <button data-testid="btn-reset-gas-halt" onClick={handleResetGasHalt}
                className="text-sm font-bold py-2 px-4 rounded-sm"
                style={{ background: "rgba(239,68,68,0.15)", color: "#ef4444", border: "1px solid rgba(239,68,68,0.3)" }}>
                Manual Override — Re-enable Withdrawals
              </button>
            </div>
          )}

          {/* AUD Cashout Guide */}
          <div className="rounded-sm p-5" style={{ background: "rgba(255,184,0,0.05)", border: "1px solid rgba(255,184,0,0.15)" }}>
            <div className="flex items-center gap-2 mb-3">
              <CreditCard size={16} style={{ color: "var(--yb-gold)" }} />
              <span className="text-xs font-bold uppercase tracking-widest" style={{ color: "var(--yb-gold)" }}>AUD Cashout Flow</span>
            </div>
            <div className="space-y-2">
              {[
                { step: "1", label: "Agent finds gold", desc: "Sentinel/Scraper/Janitor discover on-chain or off-chain opportunity" },
                { step: "2", label: "Janitor withdraws", desc: "30% (first win) or 5% (daily drip) sent to Coinspot Base L2 address" },
                { step: "3", label: "You get pinged", desc: "Telegram alert: 'Ready to Cash Out' with amount and TX hash" },
                { step: "4", label: "Withdraw to AUD", desc: "Open Coinspot → Withdraw → Use PayID for instant CommBank/Bendigo transfer" },
              ].map((s) => (
                <div key={s.step} className="flex items-start gap-3 py-2" style={{ borderBottom: "1px solid rgba(255,184,0,0.08)" }}>
                  <div className="w-5 h-5 rounded-sm flex items-center justify-center shrink-0 text-[10px] font-bold" style={{ background: "rgba(255,184,0,0.15)", color: "var(--yb-gold)" }}>
                    {s.step}
                  </div>
                  <div>
                    <p className="text-xs font-bold" style={{ color: "var(--yb-text-primary)" }}>{s.label}</p>
                    <p className="text-[11px]" style={{ color: "var(--yb-text-muted)" }}>{s.desc}</p>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-3 rounded-sm p-3" style={{ background: "rgba(255,184,0,0.08)" }}>
              <p className="text-[10px] font-bold" style={{ color: "var(--yb-gold)" }}>PayID TIP</p>
              <p className="text-[11px]" style={{ color: "var(--yb-text-secondary)" }}>
                CommBank and Bendigo both support PayID. Use it for instant AUD transfers from Coinspot.
                Avoid direct crypto deposits to bank — use the exchange as the bridge.
              </p>
            </div>
          </div>

          {/* Safety Constraints */}
          <div className="rounded-sm p-5" style={{ background: "var(--yb-surface-1)", border: "1px solid var(--yb-border)" }}>
            <p className="text-xs font-bold uppercase tracking-widest mb-4" style={{ color: "var(--yb-text-muted)" }}>Safety Constraints</p>
            <div className="space-y-3">
              {[
                { label: "Hard Gas Floor (Base)", value: `${t.gas_hard_floor_eth || 0.005} ETH`, status: t.gas_floor_halt ? "danger" : "active", icon: Fuel, desc: "Below this = ALL withdrawals frozen. No auto-resume." },
                { label: "Gas Reserve (Sui)", value: `${t.gas_reserve_sui || 5} SUI`, status: "active", icon: Fuel, desc: "Minimum balance maintained on Sui network" },
                { label: "Circuit Breaker", value: t.circuit_breaker ? "TRIGGERED" : "Armed", status: t.circuit_breaker ? "danger" : "active", icon: ShieldAlert, desc: "2 consecutive withdrawal failures = full pause" },
                { label: "Daily Max Spend", value: "$50 AUD", status: "active", icon: ShieldCheck, desc: "Operating costs capped per day" },
                { label: "Withdrawal Destination", value: t.destination_configured ? t.destination_preview : "NOT SET", status: t.destination_configured ? "active" : "warning", icon: CreditCard, desc: "Coinspot/Coinbase Base L2 (ERC-20) deposit address" },
                { label: "Telegram Alerts", value: t.telegram_configured ? "ACTIVE" : "SCAFFOLDED", status: t.telegram_configured ? "active" : "info", icon: Bell, desc: t.telegram_configured ? "Real-time alerts enabled" : "Set TELEGRAM_BOT_TOKEN + TELEGRAM_CHAT_ID in .env" },
                { label: "Network", value: (t.withdrawal_network || "base").toUpperCase(), status: "active", icon: Activity, desc: "Base L2 — ~$0.01 gas fees vs $5+ on Ethereum mainnet" },
              ].map((s) => {
                const Icon = s.icon;
                return (
                  <div key={s.label} className="flex items-center justify-between py-2" style={{ borderBottom: "1px solid var(--yb-border)" }}>
                    <div className="flex items-start gap-2">
                      <Icon size={14} className="mt-0.5 shrink-0" style={{ color: s.status === "danger" ? "#ef4444" : s.status === "warning" ? "#f59e0b" : s.status === "info" ? "#60a5fa" : "#22c55e" }} />
                      <div>
                        <p className="text-sm font-medium" style={{ color: "var(--yb-text-primary)" }}>{s.label}</p>
                        <p className="text-[10px]" style={{ color: "var(--yb-text-muted)" }}>{s.desc}</p>
                      </div>
                    </div>
                    <span className="text-xs font-mono font-bold" style={{ color: s.status === "danger" ? "#ef4444" : s.status === "warning" ? "#f59e0b" : s.status === "info" ? "#60a5fa" : "#22c55e" }}>
                      {s.value}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>

          {t.circuit_breaker && (
            <button data-testid="btn-reset-breaker" onClick={handleResetBreaker}
              className="w-full text-sm font-bold py-3 rounded-sm transition-all"
              style={{ background: "rgba(239,68,68,0.1)", color: "#ef4444", border: "1px solid rgba(239,68,68,0.2)" }}>
              Reset Circuit Breaker
            </button>
          )}

          {/* Sovereign Rules */}
          <div className="rounded-sm p-5" style={{ background: "var(--yb-surface-1)", border: "1px solid var(--yb-border)" }}>
            <p className="text-xs font-bold uppercase tracking-widest mb-3" style={{ color: "var(--yb-text-muted)" }}>Sovereign Rules (RULES.md)</p>
            <div className="space-y-2 text-xs font-mono leading-relaxed" style={{ color: "var(--yb-text-secondary)" }}>
              <p>1. Net_Profit &gt; $100 AUD → 30% initial withdrawal to Coinspot Base L2</p>
              <p>2. After Tier 1 → 5% daily drip of Net_Profit (Tier 2)</p>
              <p>3. 95% retained as working capital for gas + scaling (Tier 3)</p>
              <p>4. Hard gas floor: 0.005 ETH on Base = FULL HALT, no auto-resume</p>
              <p>5. Circuit breaker: 2 failed withdrawals = pause + Telegram emergency</p>
              <p>6. AUD cashout: Coinspot → PayID → CommBank/Bendigo (manual trigger)</p>
              <p>7. Model allocation: Gemini Flash (monitoring) / GPT-5.2 (verification)</p>
            </div>
          </div>
        </div>
      )}

      {/* Vault Audit */}
      {tab === "audit" && (
        <AuditPanel audit={audit} error={auditErr} />
      )}
    </div>
  );
}

function PayoutProposalsPanel({ proposals, error, onResolved }) {
  const [busyId, setBusyId] = useState(null);
  const [actionErr, setActionErr] = useState(null);
  const [actionMsg, setActionMsg] = useState(null);

  const pending = (proposals || []).filter((p) => p.status === "pending");
  const past = (proposals || []).filter((p) => p.status !== "pending");

  const act = async (p, kind) => {
    setActionErr(null);
    setActionMsg(null);
    if (kind === "approve" &&
        !window.confirm(`Approve payout of $${Number(p.amount).toFixed(2)} ${p.currency || "AUD"}? This sends real money via PayPal.`)) {
      return;
    }
    let reason = "";
    if (kind === "reject") {
      reason = window.prompt("Reason for rejecting this payout (optional):", "") || "";
    }
    setBusyId(p.id);
    try {
      if (kind === "approve") {
        const res = await approvePayoutProposal(p.id);
        setActionMsg(`Payout sent — $${Number(res.amount).toFixed(2)} AUD, PayPal batch ${res.payout_id}`);
      } else {
        await rejectPayoutProposal(p.id, reason);
        setActionMsg("Proposal rejected — no money moved.");
      }
    } catch (e) {
      const d = e?.response?.data?.detail;
      setActionErr(typeof d === "string" ? d : (d?.message || e.message || "Action failed"));
    } finally {
      setBusyId(null);
      onResolved && onResolved();
    }
  };

  const statusColor = (s) => (
    s === "pending" ? "var(--yb-gold)"
      : s === "executed" ? "#22c55e"
        : s === "rejected" ? "var(--yb-text-muted)"
          : "#ef4444"
  );

  return (
    <div data-testid="payout-proposals-panel" className="rounded-sm p-4"
      style={{ background: "var(--yb-surface-1)", border: `1px solid ${pending.length > 0 ? "rgba(255,184,0,0.4)" : "var(--yb-border)"}` }}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Banknote size={14} style={{ color: "var(--yb-gold)" }} />
          <span className="text-xs uppercase tracking-widest font-bold" style={{ color: "var(--yb-text-primary)" }}>
            Payout Proposals — Operator Approval Required
          </span>
        </div>
        {pending.length > 0 && (
          <span data-testid="pending-proposals-count" className="text-[10px] font-mono px-2 py-0.5 rounded-sm animate-pulse"
            style={{ color: "var(--yb-gold)", background: "rgba(255,184,0,0.1)", border: "1px solid rgba(255,184,0,0.3)" }}>
            {pending.length} PENDING
          </span>
        )}
      </div>

      <p className="text-[11px] mb-3" style={{ color: "var(--yb-text-muted)" }}>
        The treasury loop never sends money on its own — it queues proposals here. Approving one
        re-checks real balances, then executes a live PayPal payout. Rejecting it moves nothing.
      </p>

      {error && (
        <p data-testid="proposals-error" className="text-xs font-mono mb-3" style={{ color: "#ef4444" }}>
          {typeof error === "string" ? error : "Could not load payout proposals"}
        </p>
      )}
      {actionErr && (
        <p data-testid="proposal-action-error" className="text-xs font-mono mb-3 rounded-sm p-2"
          style={{ color: "#ef4444", background: "rgba(239,68,68,0.06)", border: "1px solid rgba(239,68,68,0.25)" }}>
          {actionErr}
        </p>
      )}
      {actionMsg && (
        <p data-testid="proposal-action-success" className="text-xs font-mono mb-3 rounded-sm p-2"
          style={{ color: "#22c55e", background: "rgba(34,197,94,0.06)", border: "1px solid rgba(34,197,94,0.25)" }}>
          {actionMsg}
        </p>
      )}

      {/* Pending — action required */}
      {pending.length === 0 && !error && (
        <p data-testid="no-pending-proposals" className="text-xs" style={{ color: "var(--yb-text-muted)" }}>
          No payouts awaiting approval.
        </p>
      )}
      {pending.map((p) => (
        <div key={p.id} data-testid={`proposal-pending-${p.id}`} className="rounded-sm p-4 mb-2"
          style={{ background: "rgba(255,184,0,0.05)", border: "1px solid rgba(255,184,0,0.25)" }}>
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div className="min-w-0">
              <div className="text-2xl font-black font-mono" style={{ color: "var(--yb-gold)" }}>
                ${Number(p.amount).toFixed(2)} <span className="text-xs">{p.currency || "AUD"}</span>
              </div>
              <p className="text-[11px] font-mono mt-1" style={{ color: "var(--yb-text-secondary)" }}>
                Tier {p.tier} · proposed by {p.proposed_by} · {p.created_date?.slice(0, 19)}
              </p>
              {p.note && (
                <p className="text-[11px] mt-1" style={{ color: "var(--yb-text-muted)" }}>{p.note}</p>
              )}
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <button data-testid={`btn-approve-${p.id}`} onClick={() => act(p, "approve")} disabled={busyId === p.id}
                className="text-xs font-bold px-4 py-2 rounded-sm transition-all disabled:opacity-50"
                style={{ background: "rgba(34,197,94,0.15)", color: "#22c55e", border: "1px solid rgba(34,197,94,0.35)" }}>
                {busyId === p.id ? "Working…" : "Approve & Send"}
              </button>
              <button data-testid={`btn-reject-${p.id}`} onClick={() => act(p, "reject")} disabled={busyId === p.id}
                className="text-xs font-bold px-4 py-2 rounded-sm transition-all disabled:opacity-50"
                style={{ background: "rgba(239,68,68,0.1)", color: "#ef4444", border: "1px solid rgba(239,68,68,0.25)" }}>
                Reject
              </button>
            </div>
          </div>
        </div>
      ))}

      {/* Past proposals */}
      {past.length > 0 && (
        <div className="mt-4">
          <p className="text-[10px] font-bold uppercase tracking-widest mb-2" style={{ color: "var(--yb-text-muted)" }}>
            Resolved Proposals
          </p>
          <div className="overflow-x-auto">
            <table className="w-full text-[11px] font-mono" style={{ minWidth: 640 }}>
              <thead>
                <tr className="text-left uppercase text-[9px] tracking-widest" style={{ color: "var(--yb-text-muted)" }}>
                  <th className="py-1.5 pr-3 font-normal">Amount</th>
                  <th className="py-1.5 pr-3 font-normal">Status</th>
                  <th className="py-1.5 pr-3 font-normal">Proposed by</th>
                  <th className="py-1.5 pr-3 font-normal">Resolved by</th>
                  <th className="py-1.5 pr-3 font-normal">PayPal batch</th>
                  <th className="py-1.5 pr-3 font-normal">Resolved</th>
                </tr>
              </thead>
              <tbody>
                {past.map((p) => (
                  <tr key={p.id} data-testid={`proposal-row-${p.id}`} style={{ borderTop: "1px solid var(--yb-border)" }}>
                    <td className="py-2 pr-3" style={{ color: "var(--yb-text-primary)" }}>
                      ${Number(p.amount).toFixed(2)}
                    </td>
                    <td className="py-2 pr-3 font-bold uppercase" style={{ color: statusColor(p.status) }}>
                      {p.status}
                    </td>
                    <td className="py-2 pr-3" style={{ color: "var(--yb-text-muted)" }}>{p.proposed_by || "—"}</td>
                    <td className="py-2 pr-3" style={{ color: "var(--yb-text-secondary)" }}>{p.resolved_by || "—"}</td>
                    <td className="py-2 pr-3" style={{ color: p.batch_id ? "#22c55e" : "var(--yb-text-muted)" }}>
                      {p.batch_id || "—"}
                    </td>
                    <td className="py-2 pr-3" style={{ color: "var(--yb-text-muted)" }}>
                      {p.resolved_date ? p.resolved_date.slice(0, 19) : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function RealBalanceCard({ data, error }) {
  const sources = data?.sources || {};
  const order = ["paypal", "stripe", "coinspot"];
  const labels = { paypal: "PayPal", stripe: "Stripe", coinspot: "CoinSpot" };
  const confirmed = data?.confirmed_available;
  const anyReachable = data?.any_source_reachable;
  const anyFunds = data?.any_source_with_funds;
  // "CONFIRMED" only when at least one source returns OK with available > 0.
  const status = error ? "error" : !data ? "loading" : anyFunds ? "confirmed" : anyReachable ? "zero" : "unverified";
  const statusLabel = { error: "ERROR", loading: "LOADING", confirmed: "CONFIRMED",
                        zero: "REACHABLE · $0", unverified: "UNVERIFIED" }[status];
  const statusColor = status === "confirmed" ? "#22c55e" : status === "zero" ? "#f59e0b" : "#ef4444";

  return (
    <div data-testid="real-balance-card" className="rounded-sm p-4"
      style={{ background: "var(--yb-surface-1)", border: "1px solid var(--yb-border)" }}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <ShieldCheck size={14} style={{ color: statusColor }} />
          <span className="text-xs uppercase tracking-widest font-bold" style={{ color: "var(--yb-text-primary)" }}>
            Real Balance (Live API)
          </span>
        </div>
        <span className="text-[9px] font-mono" style={{ color: statusColor }}>{statusLabel}</span>
      </div>

      {error && (
        <p className="text-xs font-mono mb-3" style={{ color: "#ef4444" }}>{error}</p>
      )}

      {data && (
        <>
          <div className="mb-3 pb-3" style={{ borderBottom: "1px solid var(--yb-border)" }}>
            <div className="text-2xl font-black font-mono" style={{ color: anyFunds ? "#22c55e" : "var(--yb-text-muted)" }}>
              ${(confirmed || 0).toFixed(2)}
            </div>
            <div className="text-[9px] uppercase tracking-widest mt-1" style={{ color: "var(--yb-text-muted)" }}>
              Confirmed Available · {data.currency} · sum of all OK sources
            </div>
          </div>

          <div className="grid gap-2"
               style={{ gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))" }}>
            {order.map((k) => {
              const s = sources[k] || { status: "unknown" };
              const ok = s.status === "ok";
              return (
                <div key={k} className="rounded-sm p-3"
                     style={{ background: "rgba(0,0,0,0.18)", border: `1px solid ${ok ? "rgba(34,197,94,0.25)" : "rgba(239,68,68,0.25)"}` }}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-[10px] font-bold uppercase tracking-widest" style={{ color: "var(--yb-text-primary)" }}>
                      {labels[k]}
                    </span>
                    <span className="text-[9px] font-mono" style={{ color: ok ? "#22c55e" : "#ef4444" }}>
                      {ok ? "OK" : "ERROR"}
                    </span>
                  </div>
                  {ok ? (
                    <>
                      <div className="text-lg font-black font-mono" style={{ color: "#22c55e" }}>
                        ${(s.available || 0).toFixed(2)}
                      </div>
                      <div className="text-[9px] uppercase tracking-widest" style={{ color: "var(--yb-text-muted)" }}>
                        Available {s.currency || "AUD"}
                      </div>
                    </>
                  ) : (
                    <div className="text-[10px] font-mono break-words" style={{ color: "#ef4444" }}>
                      {s.error || "no data"}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {!anyFunds && (
            <p className="text-[10px] mt-3" style={{ color: "#ef4444" }}>
              No real-balance source has available funds. Automated payouts are blocked.
            </p>
          )}
        </>
      )}
    </div>
  );
}

function AuditPanel({ audit, error }) {
  const [openSections, setOpenSections] = useState({ findings: true, agents: true, dupes: true, top: false, attempts: false });
  const [query, setQuery] = useState("");

  if (error) {
    return (
      <div className="rounded-sm p-5" style={{ background: "rgba(239,68,68,0.06)", border: "1px solid rgba(239,68,68,0.25)" }}>
        <div className="flex items-center gap-2 mb-2">
          <AlertTriangle size={14} style={{ color: "#ef4444" }} />
          <span className="text-xs font-bold uppercase tracking-widest" style={{ color: "#ef4444" }}>Audit Unavailable</span>
        </div>
        <p className="text-[11px] font-mono" style={{ color: "var(--yb-text-secondary)" }}>{error}</p>
      </div>
    );
  }
  if (!audit) {
    return <p className="text-xs" style={{ color: "var(--yb-text-muted)" }}>Loading vault audit…</p>;
  }

  const fmt$ = (n) => `$${Math.round(n || 0).toLocaleString()}`;
  const sevColor = (s) => ({ critical: "#ef4444", high: "#f59e0b", medium: "#60a5fa", low: "#9ca3af" }[s] || "#9ca3af");

  const kpis = [
    { idx: "01", label: "Total Income", value: fmt$(audit.total_income), accent: "var(--yb-text-primary)" },
    { idx: "02", label: "Realised", value: fmt$(audit.realised_income), accent: "#22c55e" },
    { idx: "03", label: "Projected (LLM)", value: fmt$(audit.projected_income), accent: "#ef4444", sub: `${audit.projected_pct}%` },
    { idx: "04", label: "Withdrawals", value: fmt$(audit.total_expense), accent: "#9ca3af" },
    { idx: "05", label: "Entries", value: (audit.entry_count || 0).toLocaleString(), accent: "var(--yb-text-primary)" },
    { idx: "06", label: "Live / Sandbox Tries", value: `${audit.live_attempts}/${audit.sandbox_attempts}`, accent: "var(--yb-text-primary)" },
  ];

  const matchesQuery = (s) => !query || (s || "").toLowerCase().includes(query.toLowerCase());
  const filteredDupes = (audit.duplicates || []).filter(d => matchesQuery(d.source));
  const filteredTop   = (audit.top_sources || []).filter(d => matchesQuery(d.source));

  const Section = ({ id, label, count, children }) => {
    const open = !!openSections[id];
    const Chev = open ? ChevronDown : ChevronRight;
    return (
      <div className="rounded-sm" style={{ background: "var(--yb-surface-1)", border: "1px solid var(--yb-border)" }}>
        <button onClick={() => setOpenSections(s => ({ ...s, [id]: !s[id] }))}
          className="w-full flex items-center justify-between px-4 py-3 text-left">
          <div className="flex items-center gap-2">
            <Chev size={14} style={{ color: "var(--yb-text-muted)" }} />
            <span className="text-[10px] font-bold uppercase tracking-widest" style={{ color: "var(--yb-gold)" }}>{label}</span>
          </div>
          {count !== undefined && (
            <span className="text-[10px] font-mono" style={{ color: "var(--yb-text-muted)" }}>{count}</span>
          )}
        </button>
        {open && <div className="px-4 pb-4">{children}</div>}
      </div>
    );
  };

  return (
    <div className="space-y-3">
      {/* Banner */}
      <div className="rounded-sm p-4" style={{ background: "rgba(239,68,68,0.06)", border: "1px solid rgba(239,68,68,0.25)" }}>
        <div className="flex items-center gap-2 mb-1">
          <AlertTriangle size={14} style={{ color: "#ef4444" }} />
          <span className="text-xs font-bold uppercase tracking-widest" style={{ color: "#ef4444" }}>
            Projected / Logged Entries — NOT a Confirmed Balance
          </span>
        </div>
        <p className="text-[11px]" style={{ color: "var(--yb-text-secondary)" }}>
          {audit.projected_pct}% of these entries are projections from agent leads with no receipt.
          Only entries with a real Stripe / PayPal / on-chain receipt count as realised.
          For confirmed funds, see the <span className="font-bold">Real Balance</span> card on the overview tab.
        </p>
      </div>

      {/* Adaptive KPI grid: auto-fit, no scroll */}
      <div className="grid gap-2"
           style={{ gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))" }}>
        {kpis.map((k) => (
          <div key={k.idx} className="rounded-sm p-3" style={{ background: "var(--yb-surface-1)", border: "1px solid var(--yb-border)" }}>
            <div className="flex items-center justify-between mb-1">
              <span className="text-[9px] font-bold tracking-widest" style={{ color: "var(--yb-text-muted)" }}>[{k.idx}]</span>
              {k.sub && <span className="text-[9px] font-mono" style={{ color: k.accent }}>{k.sub}</span>}
            </div>
            <div className="text-base font-black font-mono leading-tight" style={{ color: k.accent }}>{k.value}</div>
            <div className="text-[9px] uppercase tracking-widest mt-1" style={{ color: "var(--yb-text-muted)" }}>{k.label}</div>
          </div>
        ))}
      </div>

      {/* Index search */}
      <div className="rounded-sm flex items-center gap-2 px-3 py-2"
           style={{ background: "var(--yb-surface-1)", border: "1px solid var(--yb-border)" }}>
        <Search size={12} style={{ color: "var(--yb-text-muted)" }} />
        <input value={query} onChange={(e) => setQuery(e.target.value)}
          placeholder="Filter sources / agents…"
          className="flex-1 bg-transparent outline-none text-xs font-mono"
          style={{ color: "var(--yb-text-primary)" }} />
        {query && (
          <button onClick={() => setQuery("")} className="text-[10px]" style={{ color: "var(--yb-text-muted)" }}>clear</button>
        )}
      </div>

      {/* Findings */}
      <Section id="findings" label="Findings" count={`${(audit.findings || []).length} items`}>
        <div className="grid gap-2" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))" }}>
          {(audit.findings || []).map((f) => (
            <div key={f.id} className="rounded-sm p-3" style={{ background: "rgba(0,0,0,0.18)", border: `1px solid ${sevColor(f.severity)}33` }}>
              <div className="flex items-center justify-between mb-1">
                <span className="text-[9px] font-mono font-bold" style={{ color: sevColor(f.severity) }}>{f.id} · {f.severity.toUpperCase()}</span>
              </div>
              <p className="text-xs font-bold mb-1" style={{ color: "var(--yb-text-primary)" }}>{f.title}</p>
              <p className="text-[10px]" style={{ color: "var(--yb-text-muted)" }}>{f.detail}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* By agent */}
      <Section id="agents" label="Income by Agent" count={`${(audit.by_agent || []).length} agents`}>
        <div className="space-y-1">
          {(audit.by_agent || []).map((a, i) => {
            const pct = audit.total_income ? (100 * a.amount / audit.total_income) : 0;
            return (
              <div key={a.agent} className="grid items-center gap-2 py-1.5"
                   style={{ gridTemplateColumns: "32px 1fr 80px 70px 60px", borderBottom: "1px solid var(--yb-border)" }}>
                <span className="text-[9px] font-mono" style={{ color: "var(--yb-text-muted)" }}>[{String(i + 1).padStart(2, "0")}]</span>
                <span className="text-xs font-medium truncate" style={{ color: "var(--yb-text-primary)" }}>{a.agent}</span>
                <span className="text-xs font-mono text-right" style={{ color: "var(--yb-gold)" }}>{fmt$(a.amount)}</span>
                <span className="text-[10px] font-mono text-right" style={{ color: "var(--yb-text-muted)" }}>{a.count} entries</span>
                <span className="text-[10px] font-mono text-right" style={{ color: pct > 30 ? "#ef4444" : "var(--yb-text-muted)" }}>{pct.toFixed(1)}%</span>
              </div>
            );
          })}
        </div>
      </Section>

      {/* Duplicates */}
      <Section id="dupes" label="Duplicate Sources (likely fabrication)" count={`${filteredDupes.length} of ${(audit.duplicates || []).length}`}>
        {filteredDupes.length === 0 ? (
          <p className="text-[11px]" style={{ color: "var(--yb-text-muted)" }}>No duplicates match filter.</p>
        ) : (
          <div className="space-y-1">
            {filteredDupes.map((d, i) => (
              <div key={d.source} className="grid items-center gap-2 py-1.5"
                   style={{ gridTemplateColumns: "32px 1fr 80px 60px 80px", borderBottom: "1px solid var(--yb-border)" }}>
                <span className="text-[9px] font-mono" style={{ color: "var(--yb-text-muted)" }}>[{String(i + 1).padStart(2, "0")}]</span>
                <span className="text-xs truncate" style={{ color: "var(--yb-text-primary)" }} title={d.source}>{d.source}</span>
                <span className="text-xs font-mono text-right" style={{ color: "var(--yb-gold)" }}>{fmt$(d.amount)}</span>
                <span className="text-[10px] font-mono text-right" style={{ color: "#ef4444" }}>×{d.count}</span>
                <span className="text-[10px] font-mono text-right truncate" style={{ color: "var(--yb-text-muted)" }}>{d.agent}</span>
              </div>
            ))}
          </div>
        )}
      </Section>

      {/* Top sources */}
      <Section id="top" label="Top Sources" count={`${filteredTop.length} of ${(audit.top_sources || []).length}`}>
        <div className="space-y-1">
          {filteredTop.map((d, i) => (
            <div key={d.source + i} className="grid items-center gap-2 py-1.5"
                 style={{ gridTemplateColumns: "32px 1fr 80px 60px", borderBottom: "1px solid var(--yb-border)" }}>
              <span className="text-[9px] font-mono" style={{ color: "var(--yb-text-muted)" }}>[{String(i + 1).padStart(2, "0")}]</span>
              <span className="text-xs truncate" style={{ color: "var(--yb-text-primary)" }} title={d.source}>{d.source}</span>
              <span className="text-xs font-mono text-right" style={{ color: "var(--yb-gold)" }}>{fmt$(d.amount)}</span>
              <span className="text-[10px] font-mono text-right" style={{ color: "var(--yb-text-muted)" }}>×{d.count}</span>
            </div>
          ))}
        </div>
      </Section>

      {/* Withdrawal attempts */}
      <Section id="attempts" label="Withdrawal Attempts">
        <div className="grid gap-2" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))" }}>
          <div className="rounded-sm p-3" style={{ background: "rgba(0,0,0,0.18)", border: "1px solid var(--yb-border)" }}>
            <div className="text-[9px] uppercase tracking-widest" style={{ color: "var(--yb-text-muted)" }}>Live attempts</div>
            <div className="text-base font-black font-mono" style={{ color: audit.live_attempts > 0 ? "#f59e0b" : "var(--yb-text-primary)" }}>{audit.live_attempts}</div>
          </div>
          <div className="rounded-sm p-3" style={{ background: "rgba(0,0,0,0.18)", border: "1px solid var(--yb-border)" }}>
            <div className="text-[9px] uppercase tracking-widest" style={{ color: "var(--yb-text-muted)" }}>Sandbox attempts</div>
            <div className="text-base font-black font-mono" style={{ color: audit.sandbox_attempts > 0 ? "#ef4444" : "var(--yb-text-primary)" }}>{audit.sandbox_attempts}</div>
          </div>
          <div className="rounded-sm p-3" style={{ background: "rgba(0,0,0,0.18)", border: "1px solid var(--yb-border)" }}>
            <div className="text-[9px] uppercase tracking-widest" style={{ color: "var(--yb-text-muted)" }}>Successful payouts</div>
            <div className="text-base font-black font-mono" style={{ color: "var(--yb-text-primary)" }}>0</div>
          </div>
        </div>
      </Section>
    </div>
  );
}

function BankPanel({ bank, onTriggerRebate, onSettingsSaved }) {
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const b = bank || {};

  const handleRebate = async () => {
    setBusy(true); setMsg(null);
    try {
      const r = await onTriggerRebate();
      setMsg({ ok: true, text: r?.message || "Rebate processed" });
    } catch (e) {
      setMsg({ ok: false, text: e?.response?.data?.detail || "Failed" });
    } finally { setBusy(false); }
  };

  const [editing, setEditing] = useState(false);
  const [taxInput, setTaxInput] = useState("");
  const [rebateInput, setRebateInput] = useState("");
  const [saveBusy, setSaveBusy] = useState(false);
  const [saveMsg, setSaveMsg] = useState(null);

  const currentTaxPct = typeof b.tax_rate === "number"
    ? b.tax_rate * 100
    : (b.tax_rate_pct ?? 10);
  const currentRebatePct = typeof b.rebate_rate === "number"
    ? b.rebate_rate * 100
    : (b.rebate_rate_pct ?? 2);

  const startEdit = () => {
    setTaxInput(String(currentTaxPct));
    setRebateInput(String(currentRebatePct));
    setSaveMsg(null);
    setEditing(true);
  };

  const cancelEdit = () => {
    setEditing(false);
    setSaveMsg(null);
  };

  const handleSaveSettings = async () => {
    const tax = parseFloat(taxInput);
    const rebate = parseFloat(rebateInput);
    if (!Number.isFinite(tax) || tax < 0 || tax > 100) {
      setSaveMsg({ ok: false, text: "Tax rate must be between 0 and 100." });
      return;
    }
    if (!Number.isFinite(rebate) || rebate < 0 || rebate > 100) {
      setSaveMsg({ ok: false, text: "Rebate rate must be between 0 and 100." });
      return;
    }
    const confirmText =
      `Apply new tax policy?\n\n` +
      `  Tax withholding:  ${currentTaxPct}%  →  ${tax}%\n` +
      `  Quarterly rebate: ${currentRebatePct}%  →  ${rebate}%\n\n` +
      `New rates take effect immediately for new income and the next quarterly rebate. ` +
      `Existing ledger entries are NOT recomputed.`;
    if (!window.confirm(confirmText)) return;

    setSaveBusy(true); setSaveMsg(null);
    try {
      await updateBankSettings({ tax_rate_pct: tax, rebate_rate_pct: rebate });
      setSaveMsg({ ok: true, text: `Saved: tax ${tax}% / rebate ${rebate}%.` });
      setEditing(false);
      if (onSettingsSaved) await onSettingsSaved();
    } catch (e) {
      setSaveMsg({ ok: false, text: e?.response?.data?.detail || "Failed to save settings" });
    } finally {
      setSaveBusy(false);
    }
  };

  const fmt = (n) => (typeof n === "number" ? `$${n.toFixed(4)}` : "—");
  const fmtDate = (d) => d ? new Date(d).toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" }) : "—";

  const reservePct = b.gross_income > 0 ? Math.min(100, (b.reserve_balance / b.gross_income) * 100) : 0;

  return (
    <div data-testid="bank-panel" className="space-y-3">
      {/* Summary Cards */}
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        {[
          { v: fmt(b.gross_income), l: "Gross Income", c: "#22c55e" },
          { v: fmt(b.total_withheld), l: `Tax Withheld (${b.tax_rate_pct ?? 10}%)`, c: "#f59e0b" },
          { v: fmt(b.reserve_balance), l: "Tax Reserve Balance", c: "#60a5fa" },
          { v: fmt(b.net_profit_posttax), l: "Net Profit (Post-Tax)", c: "var(--yb-gold)" },
        ].map((s) => (
          <div key={s.l} className="rounded-sm p-4" style={{ background: "var(--yb-surface-1)", border: "1px solid var(--yb-border)" }}>
            <div className="text-xl font-black font-mono" style={{ color: s.c }}>{s.v}</div>
            <div className="text-[9px] uppercase tracking-widest mt-1" style={{ color: "var(--yb-text-muted)" }}>{s.l}</div>
          </div>
        ))}
      </div>

      {/* Reserve Balance Bar */}
      <div className="rounded-sm p-4" style={{ background: "var(--yb-surface-1)", border: "1px solid var(--yb-border)" }}>
        <div className="flex items-center justify-between mb-2">
          <p className="text-[10px] font-bold uppercase tracking-widest" style={{ color: "#60a5fa" }}>Tax Reserve</p>
          <span className="text-xs font-mono" style={{ color: "var(--yb-text-muted)" }}>{reservePct.toFixed(1)}% of gross</span>
        </div>
        <div className="h-2.5 rounded-sm overflow-hidden" style={{ background: "rgba(255,255,255,0.06)" }}>
          <div className="h-full rounded-sm transition-all" style={{ width: `${reservePct}%`, background: "#60a5fa" }} />
        </div>
        <div className="flex justify-between mt-2 text-[10px] font-mono">
          <span style={{ color: "#f59e0b" }}>Withheld: {fmt(b.total_withheld)}</span>
          <span style={{ color: "#22c55e" }}>Rebated: {fmt(b.total_rebated)}</span>
          <span style={{ color: "#60a5fa" }}>Reserve: {fmt(b.reserve_balance)}</span>
        </div>
      </div>

      {/* Tax Policy Settings */}
      <div data-testid="bank-settings-card" className="rounded-sm p-4"
           style={{ background: "rgba(255,184,0,0.04)", border: "1px solid rgba(255,184,0,0.15)" }}>
        <div className="flex items-center gap-2 mb-3">
          <SettingsIcon size={14} style={{ color: "var(--yb-gold)" }} />
          <span className="text-xs font-bold uppercase tracking-widest" style={{ color: "var(--yb-gold)" }}>
            Tax Policy
          </span>
          {!editing && (
            <button data-testid="btn-edit-bank-settings" onClick={startEdit}
              className="ml-auto text-[10px] font-medium px-2 py-1 rounded-sm"
              style={{ background: "rgba(255,255,255,0.04)", color: "var(--yb-text-secondary)", border: "1px solid var(--yb-border)" }}>
              Edit Rates
            </button>
          )}
        </div>

        {!editing ? (
          <div className="grid grid-cols-2 gap-3">
            <div>
              <p className="text-[10px]" style={{ color: "var(--yb-text-muted)" }}>Tax Withholding</p>
              <p data-testid="text-tax-rate" className="text-sm font-bold font-mono" style={{ color: "var(--yb-text-primary)" }}>
                {currentTaxPct}% per income event
              </p>
            </div>
            <div>
              <p className="text-[10px]" style={{ color: "var(--yb-text-muted)" }}>Quarterly Rebate</p>
              <p data-testid="text-rebate-rate" className="text-sm font-bold font-mono" style={{ color: "var(--yb-text-primary)" }}>
                {currentRebatePct}% of quarterly gross
              </p>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[10px] uppercase tracking-widest" style={{ color: "var(--yb-text-muted)" }}>
                  Tax Rate (%)
                </label>
                <input type="number" min="0" max="100" step="0.01" value={taxInput}
                  onChange={(e) => setTaxInput(e.target.value)}
                  data-testid="input-tax-rate"
                  className="mt-1 w-full px-3 py-2 text-sm rounded-sm font-mono"
                  style={{ background: "rgba(0,0,0,0.3)", border: "1px solid var(--yb-border)", color: "var(--yb-text-primary)" }}
                />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-widest" style={{ color: "var(--yb-text-muted)" }}>
                  Rebate Rate (%)
                </label>
                <input type="number" min="0" max="100" step="0.01" value={rebateInput}
                  onChange={(e) => setRebateInput(e.target.value)}
                  data-testid="input-rebate-rate"
                  className="mt-1 w-full px-3 py-2 text-sm rounded-sm font-mono"
                  style={{ background: "rgba(0,0,0,0.3)", border: "1px solid var(--yb-border)", color: "var(--yb-text-primary)" }}
                />
              </div>
            </div>
            <p className="text-[11px]" style={{ color: "var(--yb-text-muted)" }}>
              New rates apply to future income and the next quarterly rebate. Existing entries stay as recorded.
            </p>
            <div className="flex gap-2">
              <button onClick={handleSaveSettings} disabled={saveBusy}
                data-testid="btn-save-bank-settings"
                className="text-xs font-bold px-4 py-2 rounded-sm"
                style={{ background: "var(--yb-gold)", color: "#000", opacity: saveBusy ? 0.6 : 1 }}>
                {saveBusy ? "Saving…" : "Save Rates"}
              </button>
              <button onClick={cancelEdit} disabled={saveBusy}
                data-testid="btn-cancel-bank-settings"
                className="text-xs font-medium px-4 py-2 rounded-sm"
                style={{ background: "rgba(255,255,255,0.04)", color: "var(--yb-text-secondary)", border: "1px solid var(--yb-border)" }}>
                Cancel
              </button>
            </div>
          </div>
        )}
        {saveMsg && (
          <p data-testid="bank-settings-msg" className="text-[11px] font-mono mt-2"
             style={{ color: saveMsg.ok ? "#22c55e" : "#ef4444" }}>{saveMsg.text}</p>
        )}
      </div>

      {/* Quarterly Rebate Info */}
      <div className="rounded-sm p-4" style={{ background: "rgba(96,165,250,0.05)", border: "1px solid rgba(96,165,250,0.15)" }}>
        <div className="flex items-center gap-2 mb-3">
          <CalendarClock size={14} style={{ color: "#60a5fa" }} />
          <span className="text-xs font-bold uppercase tracking-widest" style={{ color: "#60a5fa" }}>Quarterly Rebate</span>
          <span className="ml-auto text-[10px] font-mono px-2 py-0.5 rounded-sm" style={{ background: "rgba(96,165,250,0.1)", color: "#60a5fa", border: "1px solid rgba(96,165,250,0.2)" }}>
            {b.rebate_rate_pct ?? 2}% of quarterly gross
          </span>
        </div>
        <div className="grid grid-cols-2 gap-3 mb-3">
          <div>
            <p className="text-[10px]" style={{ color: "var(--yb-text-muted)" }}>Current Quarter</p>
            <p className="text-sm font-bold font-mono" style={{ color: "var(--yb-text-primary)" }}>{b.current_quarter || "—"}</p>
          </div>
          <div>
            <p className="text-[10px]" style={{ color: "var(--yb-text-muted)" }}>Next Rebate Date</p>
            <p className="text-sm font-bold font-mono" style={{ color: "var(--yb-text-primary)" }}>{fmtDate(b.next_rebate_date)}</p>
          </div>
          <div>
            <p className="text-[10px]" style={{ color: "var(--yb-text-muted)" }}>Last Rebate Amount</p>
            <p className="text-sm font-bold font-mono" style={{ color: "#22c55e" }}>
              {b.last_rebate ? fmt(b.last_rebate.rebate_amount) : "None yet"}
            </p>
          </div>
          <div>
            <p className="text-[10px]" style={{ color: "var(--yb-text-muted)" }}>Last Rebate Quarter</p>
            <p className="text-sm font-bold font-mono" style={{ color: "var(--yb-text-primary)" }}>
              {b.last_rebate?.quarter_key || "None yet"}
            </p>
          </div>
        </div>
        <button onClick={handleRebate} disabled={busy}
          className="text-xs font-bold px-4 py-2 rounded-sm transition-all"
          style={{ background: "rgba(96,165,250,0.1)", color: "#60a5fa", border: "1px solid rgba(96,165,250,0.2)", opacity: busy ? 0.6 : 1 }}>
          {busy ? "Processing…" : "Process Previous Quarter Rebate"}
        </button>
        {msg && (
          <p className="text-[11px] font-mono mt-2" style={{ color: msg.ok ? "#22c55e" : "#ef4444" }}>{msg.text}</p>
        )}
      </div>

      {/* Rebate History */}
      <div className="rounded-sm p-4" style={{ background: "var(--yb-surface-1)", border: "1px solid var(--yb-border)" }}>
        <p className="text-[10px] font-bold uppercase tracking-widest mb-3" style={{ color: "var(--yb-text-muted)" }}>Rebate History</p>
        {(b.rebate_history || []).length === 0 ? (
          <p className="text-xs" style={{ color: "var(--yb-text-muted)" }}>No rebates yet. The first rebate runs at the start of the next calendar quarter.</p>
        ) : (
          <div className="space-y-1.5">
            {(b.rebate_history || []).map((r, i) => (
              <div key={i} className="flex items-center gap-4 py-2" style={{ borderBottom: "1px solid var(--yb-border)" }}>
                <PiggyBank size={14} style={{ color: "#22c55e" }} />
                <div className="flex-1">
                  <p className="text-sm font-bold" style={{ color: "var(--yb-text-primary)" }}>{r.quarter_key}</p>
                  <p className="text-[10px] font-mono" style={{ color: "var(--yb-text-muted)" }}>
                    Gross: {fmt(r.gross_income)} · {fmtDate(r.created_date)}
                  </p>
                </div>
                <div className="text-lg font-black font-mono" style={{ color: "#22c55e" }}>
                  +{fmt(r.rebate_amount)}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* How it works */}
      <div className="rounded-sm p-4" style={{ background: "var(--yb-surface-1)", border: "1px solid var(--yb-border)" }}>
        <p className="text-[10px] font-bold uppercase tracking-widest mb-3" style={{ color: "var(--yb-text-muted)" }}>How Tax Bank Works</p>
        <div className="space-y-2 text-xs leading-relaxed" style={{ color: "var(--yb-text-secondary)" }}>
          <p><span style={{ color: "#f59e0b" }}>10% withheld</span> — Every income event writes a paired withholding entry to the Bank reserve.</p>
          <p><span style={{ color: "var(--yb-gold)" }}>Post-tax balance</span> — Net profit and all payout tiers are calculated from gross income minus tax and expenses.</p>
          <p><span style={{ color: "#22c55e" }}>2% quarterly rebate</span> — At the start of each calendar quarter, 2% of last quarter's gross income is credited back.</p>
          <p><span style={{ color: "#60a5fa" }}>Historical entries safe</span> — Tax only applies to income recorded after this feature shipped.</p>
        </div>
      </div>
    </div>
  );
}

function ManualWithdraw({ onDone }) {
  const [ppAmount, setPpAmount] = useState("");
  const [ppMsg, setPpMsg] = useState(null);
  const [ppBusy, setPpBusy] = useState(false);

  const [coin, setCoin] = useState("BTC");
  const [csAmount, setCsAmount] = useState("");
  const [csAddress, setCsAddress] = useState("");
  const [csMsg, setCsMsg] = useState(null);
  const [csBusy, setCsBusy] = useState(false);

  const [balances, setBalances] = useState(null);

  const handlePaypal = async () => {
    setPpMsg(null); setPpBusy(true);
    try {
      const amt = parseFloat(ppAmount);
      if (!(amt > 0)) throw new Error("Enter a positive AUD amount.");
      const r = await withdrawPaypal(amt);
      setPpMsg({ ok: true, text: `Sent $${r.amount} AUD via PayPal. Batch: ${r.payout_id}` });
      setPpAmount("");
      onDone && onDone();
    } catch (e) {
      setPpMsg({ ok: false, text: e?.response?.data?.detail || e.message });
    } finally { setPpBusy(false); }
  };

  const handleCoinspot = async () => {
    setCsMsg(null); setCsBusy(true);
    try {
      const amt = parseFloat(csAmount);
      if (!coin || !csAddress || !(amt > 0)) throw new Error("Coin, destination address and positive amount required.");
      const r = await withdrawCoinspot({ cointype: coin, amount: amt, address: csAddress });
      setCsMsg({ ok: true, text: `Withdrawal queued. TX: ${r.tx}` });
      setCsAmount(""); setCsAddress("");
      onDone && onDone();
    } catch (e) {
      setCsMsg({ ok: false, text: e?.response?.data?.detail || e.message });
    } finally { setCsBusy(false); }
  };

  const handleSync = async () => {
    try {
      await syncCoinspot();
      const b = await coinspotBalances();
      setBalances(b);
    } catch (e) {
      setBalances({ error: e?.response?.data?.detail || e.message });
    }
  };

  const inputStyle = {
    background: "rgba(0,0,0,0.3)",
    border: "1px solid var(--yb-border)",
    color: "var(--yb-text-primary)",
  };

  return (
    <div className="space-y-3">
      {/* PayPal AUD payout */}
      <div data-testid="withdraw-paypal-card" className="rounded-sm p-5"
           style={{ background: "var(--yb-surface-1)", border: "1px solid var(--yb-border)" }}>
        <div className="flex items-center gap-2 mb-3">
          <Banknote size={16} style={{ color: "var(--yb-gold)" }} />
          <span className="text-xs font-bold uppercase tracking-widest" style={{ color: "var(--yb-gold)" }}>
            PayPal — Fiat Payout (AUD)
          </span>
        </div>
        <p className="text-[11px] mb-3" style={{ color: "var(--yb-text-muted)" }}>
          Sends to the email in <code>PAYPAL_RECEIVER_EMAIL</code>. Minimum $1.00 AUD.
        </p>
        <div className="flex gap-2">
          <input type="number" min="1" step="0.01" value={ppAmount}
                 onChange={(e) => setPpAmount(e.target.value)}
                 placeholder="Amount AUD"
                 className="flex-1 px-3 py-2 text-sm rounded-sm font-mono"
                 style={inputStyle}
                 data-testid="input-paypal-amount" />
          <button onClick={handlePaypal} disabled={ppBusy}
                  data-testid="btn-paypal-withdraw"
                  className="text-xs font-bold px-4 py-2 rounded-sm"
                  style={{ background: "var(--yb-gold)", color: "#000", opacity: ppBusy ? 0.6 : 1 }}>
            {ppBusy ? "Sending..." : "Send Payout"}
          </button>
        </div>
        {ppMsg && (
          <p className="text-[11px] font-mono mt-2"
             style={{ color: ppMsg.ok ? "#22c55e" : "#ef4444" }}>{ppMsg.text}</p>
        )}
      </div>

      {/* CoinSpot crypto withdrawal */}
      <div data-testid="withdraw-coinspot-card" className="rounded-sm p-5"
           style={{ background: "var(--yb-surface-1)", border: "1px solid var(--yb-border)" }}>
        <div className="flex items-center gap-2 mb-3">
          <ArrowDownToLine size={16} style={{ color: "var(--yb-gold)" }} />
          <span className="text-xs font-bold uppercase tracking-widest" style={{ color: "var(--yb-gold)" }}>
            CoinSpot — Crypto Withdrawal
          </span>
          <button onClick={handleSync}
                  className="ml-auto text-[10px] font-medium px-2 py-1 rounded-sm"
                  style={{ background: "rgba(255,255,255,0.04)", color: "var(--yb-text-secondary)", border: "1px solid var(--yb-border)" }}>
            <RefreshCw size={10} className="inline mr-1" /> Sync Balances
          </button>
        </div>
        <p className="text-[11px] mb-3" style={{ color: "var(--yb-text-muted)" }}>
          Uses your CoinSpot API key (withdraw scope required). Withdrawal address must be pre-whitelisted in CoinSpot.
        </p>
        <div className="grid grid-cols-12 gap-2">
          <select value={coin} onChange={(e) => setCoin(e.target.value)}
                  data-testid="select-coinspot-coin"
                  className="col-span-2 px-2 py-2 text-sm rounded-sm font-mono"
                  style={inputStyle}>
            {["BTC", "ETH", "USDT", "USDC", "SUI", "SOL", "DOGE", "XRP", "LTC"].map(c => <option key={c}>{c}</option>)}
          </select>
          <input type="number" min="0" step="0.00000001" value={csAmount}
                 onChange={(e) => setCsAmount(e.target.value)}
                 placeholder="Amount"
                 className="col-span-3 px-3 py-2 text-sm rounded-sm font-mono"
                 style={inputStyle}
                 data-testid="input-coinspot-amount" />
          <input type="text" value={csAddress}
                 onChange={(e) => setCsAddress(e.target.value)}
                 placeholder="Destination address"
                 className="col-span-5 px-3 py-2 text-sm rounded-sm font-mono"
                 style={inputStyle}
                 data-testid="input-coinspot-address" />
          <button onClick={handleCoinspot} disabled={csBusy}
                  data-testid="btn-coinspot-withdraw"
                  className="col-span-2 text-xs font-bold px-3 py-2 rounded-sm"
                  style={{ background: "var(--yb-gold)", color: "#000", opacity: csBusy ? 0.6 : 1 }}>
            {csBusy ? "Sending..." : "Withdraw"}
          </button>
        </div>
        {csMsg && (
          <p className="text-[11px] font-mono mt-2"
             style={{ color: csMsg.ok ? "#22c55e" : "#ef4444" }}>{csMsg.text}</p>
        )}
        {balances && (
          <pre className="mt-3 text-[10px] font-mono p-3 rounded-sm overflow-x-auto"
               style={{ background: "rgba(0,0,0,0.3)", color: "var(--yb-text-secondary)", maxHeight: 160 }}>
{JSON.stringify(balances, null, 2)}
          </pre>
        )}
      </div>
    </div>
  );
}

function YieldOverviewCard({ data, error }) {
  if (error) return (
    <div data-testid="yield-overview-card" className="rounded-sm p-4"
      style={{ background: "var(--yb-surface-1)", border: "1px solid rgba(239,68,68,0.25)" }}>
      <p className="text-[10px] font-bold uppercase tracking-widest mb-1" style={{ color: "#ef4444" }}>
        Stablecoin Yield
      </p>
      <p className="text-[11px] font-mono" style={{ color: "#ef4444" }} data-testid="yield-card-error">{error}</p>
    </div>
  );
  if (!data) return null;
  const enabled = data.enabled;
  const sweep = data.settings?.sweep_threshold_aud || 0;
  const deployed = data.deployed_usdc || 0;
  const pct = sweep > 0 ? Math.min(100, (deployed / sweep) * 100) : 0;
  return (
    <div data-testid="yield-overview-card" className="rounded-sm p-4"
      style={{ background: "var(--yb-surface-1)", border: "1px solid var(--yb-border)" }}>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <PiggyBank size={14} style={{ color: "var(--yb-gold)" }} />
          <span className="text-xs uppercase tracking-widest" style={{ color: "var(--yb-text-muted)" }}>
            Stablecoin Yield (own-funds)
          </span>
        </div>
        <span className="text-[9px] font-mono px-2 py-0.5 rounded-sm"
          style={{
            color: enabled ? "#22c55e" : "var(--yb-text-muted)",
            background: enabled ? "rgba(34,197,94,0.1)" : "rgba(255,255,255,0.04)",
            border: `1px solid ${enabled ? "rgba(34,197,94,0.2)" : "var(--yb-border)"}`,
          }}>
          {enabled ? "ENABLED" : "DISABLED"}
        </span>
      </div>
      <div className="grid grid-cols-4 gap-3">
        <div>
          <div className="text-lg font-black font-mono" style={{ color: "var(--yb-text-primary)" }}
               data-testid="yield-current-protocol">
            {data.current_protocol || "—"}
          </div>
          <div className="text-[9px] uppercase tracking-widest mt-1" style={{ color: "var(--yb-text-muted)" }}>Protocol</div>
        </div>
        <div>
          <div className="text-lg font-black font-mono" style={{ color: "#22c55e" }}>
            ${deployed.toFixed(2)}
          </div>
          <div className="text-[9px] uppercase tracking-widest mt-1" style={{ color: "var(--yb-text-muted)" }}>Deployed USDC</div>
        </div>
        <div>
          <div className="text-lg font-black font-mono" style={{ color: "var(--yb-gold)" }}>
            ${(data.yield_30d_aud || 0).toFixed(2)}
          </div>
          <div className="text-[9px] uppercase tracking-widest mt-1" style={{ color: "var(--yb-text-muted)" }}>Yield (30d)</div>
        </div>
        <div>
          <div className="text-lg font-black font-mono" style={{ color: "var(--yb-text-primary)" }}>
            {data.open_positions || 0}
          </div>
          <div className="text-[9px] uppercase tracking-widest mt-1" style={{ color: "var(--yb-text-muted)" }}>Positions</div>
        </div>
      </div>
      <div className="mt-3">
        <div className="flex justify-between text-[10px] font-mono mb-1">
          <span style={{ color: "var(--yb-text-muted)" }}>Sweep threshold progress</span>
          <span style={{ color: "var(--yb-gold)" }}>${deployed.toFixed(0)} / ${sweep.toFixed(0)}</span>
        </div>
        <div className="h-2 rounded-sm overflow-hidden" style={{ background: "rgba(255,255,255,0.05)" }}>
          <div className="h-full rounded-sm" style={{ width: `${pct}%`, background: "var(--yb-gold)" }} />
        </div>
      </div>
    </div>
  );
}

function YieldPanel({ data, error, onChange }) {
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const [edits, setEdits] = useState({});

  if (error) return (
    <div className="rounded-sm p-4" style={{ background: "var(--yb-surface-1)", border: "1px solid rgba(239,68,68,0.25)" }}>
      <p className="text-xs font-mono" style={{ color: "#ef4444" }}>{error}</p>
    </div>
  );
  if (!data) return null;
  const s = { ...data.settings, ...edits };

  const save = async () => {
    setBusy(true); setMsg(null);
    try {
      await updateYieldSettings(edits);
      setEdits({});
      setMsg({ ok: true, text: "Settings saved" });
      onChange && onChange();
    } catch (e) {
      setMsg({ ok: false, text: e?.response?.data?.detail || e.message });
    } finally { setBusy(false); }
  };

  const runNow = async () => {
    setBusy(true); setMsg(null);
    try {
      const r = await runYieldCycle();
      setMsg({ ok: r.ran, text: JSON.stringify(r) });
      onChange && onChange();
    } catch (e) {
      setMsg({ ok: false, text: e?.response?.data?.detail || e.message });
    } finally { setBusy(false); }
  };

  const inputStyle = { background: "rgba(255,255,255,0.04)", border: "1px solid var(--yb-border)", color: "var(--yb-text-primary)" };

  return (
    <div data-testid="yield-panel" className="space-y-4">
      <div className="rounded-sm p-4" style={{ background: "rgba(239,68,68,0.06)", border: "1px solid rgba(239,68,68,0.25)" }}>
        <p className="text-[11px]" style={{ color: "var(--yb-text-secondary)" }}>
          <span className="font-bold">Own-funds only.</span> The router never moves end-user balances.
          End-user opt-in toggles record consent only and are not read by any execution path until
          AFS/AUSTRAC review is complete.
        </p>
      </div>

      <div className="rounded-sm p-4" style={{ background: "var(--yb-surface-1)", border: "1px solid var(--yb-border)" }}>
        <div className="flex items-center justify-between mb-3">
          <p className="text-[10px] font-bold uppercase tracking-widest" style={{ color: "var(--yb-gold)" }}>Settings</p>
          <label className="flex items-center gap-2 text-xs cursor-pointer" style={{ color: "var(--yb-text-primary)" }}>
            <input type="checkbox" checked={!!s.enabled}
              onChange={(e) => setEdits({ ...edits, enabled: e.target.checked })}
              data-testid="yield-toggle-enabled" />
            Enable yield router
          </label>
        </div>
        <div className="grid grid-cols-2 gap-3">
          {[
            ["min_reserve_aud", "Min reserve (AUD)"],
            ["min_deploy_aud", "Min deploy (AUD)"],
            ["sweep_threshold_aud", "Sweep threshold (AUD)"],
            ["min_net_apy_pct", "Min net APY (%)"],
          ].map(([k, label]) => (
            <div key={k}>
              <p className="text-[10px] uppercase tracking-widest mb-1" style={{ color: "var(--yb-text-muted)" }}>{label}</p>
              <input type="number" value={s[k] ?? 0}
                onChange={(e) => setEdits({ ...edits, [k]: parseFloat(e.target.value) })}
                className="w-full px-3 py-2 text-sm rounded-sm font-mono"
                style={inputStyle}
                data-testid={`yield-input-${k}`} />
            </div>
          ))}
        </div>
        <div className="mt-3 flex items-center gap-2">
          <button data-testid="btn-yield-save" onClick={save} disabled={busy || Object.keys(edits).length === 0}
            className="text-xs font-bold px-4 py-2 rounded-sm"
            style={{ background: "var(--yb-gold)", color: "#000", opacity: (busy || Object.keys(edits).length === 0) ? 0.5 : 1 }}>
            Save settings
          </button>
          <button data-testid="btn-yield-run-now" onClick={runNow} disabled={busy}
            className="text-xs font-medium px-3 py-2 rounded-sm"
            style={{ background: "rgba(255,255,255,0.04)", color: "var(--yb-text-secondary)", border: "1px solid var(--yb-border)" }}>
            Run yield cycle now
          </button>
          {msg && <span className="text-[11px] font-mono" style={{ color: msg.ok ? "#22c55e" : "#ef4444" }}>{msg.text.slice(0, 200)}</span>}
        </div>
      </div>

      <div className="rounded-sm p-4" style={{ background: "var(--yb-surface-1)", border: "1px solid var(--yb-border)" }}>
        <p className="text-[10px] font-bold uppercase tracking-widest mb-2" style={{ color: "var(--yb-gold)" }}>Status</p>
        <pre className="text-[10px] font-mono p-3 rounded-sm overflow-x-auto"
          style={{ background: "rgba(0,0,0,0.3)", color: "var(--yb-text-secondary)", maxHeight: 280 }}>
{JSON.stringify(data, null, 2)}
        </pre>
      </div>
    </div>
  );
}
